import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getStats, getUser, saveUser, getStreak, getQuizScores, getProgress, getMatchScores, getMorphemeScores, logout, clearAllUserData, getUserTrialState } from '@/utils/storage';
import { signOut, updateProfile, sendPasswordResetEmail } from 'firebase/auth';
import { collection, query, where, getDocs, writeBatch, doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '@/firebase/config';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import { checkIsPro, getPreviewRole } from '@/utils/planAccess';
import { generateProgressPdf } from '@/utils/generateProgressPdf';
import { getAllTerms } from '@/data/medicalTerms';

export const Profile = () => {
  const navigate = useNavigate();
  const { currentLanguage, setLanguage, t } = useLanguage();
  const isTr = currentLanguage !== 'en';
  const user = getUser();
  const stats = getStats();
  const streak = getStreak();
  const quizScores = getQuizScores();

  // ----------------------------------------------------
  // States
  // ----------------------------------------------------
  const previewRole = getPreviewRole();
  const effectiveUser = previewRole
    ? { uid: 'preview-uid', name: 'Dr. Ahmet Kaya', email: 'dr.kaya@healthlexmed.com', joinDate: '2026-03-01T00:00:00.000Z' }
    : user;
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editName, setEditName] = useState(effectiveUser?.name || '');
  const [editEmail, setEditEmail] = useState(effectiveUser?.email || '');
  const [saveLoading, setSaveLoading] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  // Subscription & Pro State
  const [isPro, setIsPro] = useState(previewRole === 'pro' || previewRole === 'lifetime');
  const [userDocData, setUserDocData] = useState(() => {
    if (previewRole === 'lifetime') {
      return { isLifetime: true, isPro: true, planType: 'lifetime', subscriptionStatus: 'active' };
    }
    if (previewRole === 'basic') {
      return { isBasic: true, isPro: false, planType: 'basic', subscriptionStatus: 'active' };
    }
    if (previewRole === 'pro') {
      return { isPro: true, planType: 'pro', subscriptionStatus: 'active' };
    }
    return null;
  });

  const effectiveUserData = userDocData || getUser();
  const subStatus = (effectiveUserData?.subscriptionStatus || '').toLowerCase();
  const planName = (effectiveUserData?.planType || effectiveUserData?.plan || '').toLowerCase();

  const isLifetime =
    previewRole === 'lifetime' ||
    effectiveUserData?.isLifetime === true ||
    planName.includes('lifetime') ||
    subStatus === 'lifetime';

  const isBasic =
    !isLifetime &&
    (previewRole === 'basic' ||
      effectiveUserData?.isBasic === true ||
      planName.includes('basic') ||
      subStatus === 'basic');

  const trialState = getUserTrialState(
    previewRole === 'trial'
      ? { ...effectiveUserData, subscriptionStatus: 'trialing', isTrial: true }
      : effectiveUserData
  );
  const isTrialActive = trialState.hasTrial && trialState.isActive && !trialState.isExpired;
  const trialDaysLeft = trialState.daysLeft;

  const isProPlan =
    !isLifetime &&
    !isBasic &&
    (previewRole === 'pro' ||
      effectiveUserData?.isPro === true ||
      isPro ||
      subStatus === 'active' ||
      subStatus === 'pro');

  // Aktif yinelenen abonelik (Temel veya Pro; Ömür Boyu hariç):
  const hasActiveSubscription =
    !isLifetime &&
    (subStatus === 'active' ||
      subStatus === 'trialing' ||
      subStatus === 'past_due' ||
      (previewRole && (previewRole === 'pro' || previewRole === 'basic')) ||
      Boolean(effectiveUserData?.paddleSubscriptionId));

  useEffect(() => {
    if (previewRole) return;
    const uid = auth?.currentUser?.uid || user?.uid;
    if (!uid) {
      const localUser = getUser();
      setIsPro(checkIsPro(localUser));
      return;
    }

    try {
      const userDocRef = doc(db, 'users', uid);
      const unsub = onSnapshot(userDocRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserDocData(data);
          setIsPro(checkIsPro(data));
        } else {
          setIsPro(false);
        }
      }, (err) => {
        console.warn('[Profile] Could not fetch user doc:', err);
      });
      return () => unsub();
    } catch (e) {
      console.warn('[Profile] Firestore snapshot error:', e);
    }
  }, [user, previewRole]);

  // Toggle States
  const [emailNotifications, setEmailNotifications] = useState(false);

  // Showcase pins state (Max 3 pins)
  const [pinnedBadges, setPinnedBadges] = useState(['first_step', 'quick_start']);

  // Load preferences from localStorage on mount
  useEffect(() => {
    const savedPrefs = localStorage.getItem('user_preferences');
    if (savedPrefs) {
      try {
        const parsed = JSON.parse(savedPrefs);
        if (parsed.emailNotifications !== undefined) setEmailNotifications(parsed.emailNotifications);
        if (parsed.pinnedBadges) setPinnedBadges(parsed.pinnedBadges);
      } catch (err) {
        console.error('Error parsing user preferences:', err);
      }
    }
  }, []);

  // Save preferences helper
  const savePreferences = (updated) => {
    const current = {
      emailNotifications,
      pinnedBadges,
      ...updated
    };
    localStorage.setItem('user_preferences', JSON.stringify(current));
  };

  // ----------------------------------------------------
  // Username casing formatter
  // ----------------------------------------------------
  const formatName = (name) => {
    if (!name) return currentLanguage === 'en' ? 'User' : 'Kullanıcı';
    return name
      .split(' ')
      .map(word => {
        if (!word) return '';
        return word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1).toLocaleLowerCase('tr-TR');
      })
      .join(' ');
  };
  const userName = formatName(effectiveUser?.name || user?.name);

  const formatDate = (dateStr) => {
    if (!dateStr) return currentLanguage === 'en' ? 'June 15, 2026' : '15 Haziran 2026';
    return new Date(dateStr).toLocaleDateString(currentLanguage === 'en' ? 'en-US' : 'tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // ----------------------------------------------------
  // Handlers
  // ----------------------------------------------------
  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
    }
    logout();
    toast.success(t('loggedOut', 'Çıkış yapıldı'));
    navigate('/login');
  };

  const handleSaveChanges = async () => {
    if (!editName.trim()) {
      toast.error(t('nameRequired', 'İsim alanı boş bırakılamaz.'));
      return;
    }
    try {
      setSaveLoading(true);
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, { displayName: editName });
      }
      saveUser({
        ...user,
        name: editName,
        email: editEmail
      });
      toast.success(t('profileUpdated', 'Profil başarıyla güncellendi!'));
      setIsEditOpen(false);
    } catch (err) {
      console.error('Update profile error:', err);
      toast.error(t('profileUpdateError', 'Profil güncellenirken bir sorun oluştu.'));
    } finally {
      setSaveLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!user?.email) return;
    try {
      await sendPasswordResetEmail(auth, user.email);
      toast.success(t('resetEmailSent', 'Şifre sıfırlama e-postası gönderildi!'));
    } catch (err) {
      console.error('Reset password error:', err);
      toast.error(t('resetEmailError', 'Şifre sıfırlama e-postası gönderilemedi.'));
    }
  };

  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const handleExportPdf = async () => {
    try {
      setIsExportingPdf(true);
      toast.loading(isTr ? 'İlerleme raporunuz PDF olarak hazırlanıyor...' : 'Generating progress report PDF...', { id: 'export-pdf-toast' });

      const terms = getAllTerms();
      const planTitle = isLifetime ? 'Ömür Boyu (Lifetime)' : isProPlan ? 'Pro Plan' : isBasic ? 'Temel Plan' : isTrialActive ? 'Deneme (Trial)' : 'Ücretsiz (Guest)';

      await generateProgressPdf({
        user: effectiveUser,
        stats,
        streak,
        progress: getProgress(),
        terms,
        quizScores: getQuizScores(),
        matchScores: getMatchScores(),
        morphemeScores: getMorphemeScores(),
        planName: planTitle,
        isTr
      });

      toast.success(isTr ? 'PDF raporu başarıyla indirildi!' : 'PDF report downloaded successfully!', { id: 'export-pdf-toast' });
    } catch (err) {
      console.error('[Profile] PDF export error:', err);
      toast.error(isTr ? 'PDF raporu oluşturulurken bir hata oluştu.' : 'Failed to generate PDF report.', { id: 'export-pdf-toast' });
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleExportJson = () => {
    const data = {
      user: effectiveUser,
      stats,
      progress: getProgress(),
      quizScores: getQuizScores(),
      matchScores: getMatchScores(),
      morphemeScores: getMorphemeScores()
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const cleanName = (effectiveUser?.name || 'user').replace(/[\s\/\\]+/g, '_');
    link.download = `HealthLex-Ham-Veri-${cleanName}.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(t('dataDownloaded', 'Ham verileriniz JSON olarak indirildi.'));
  };

  const [portalLoading, setPortalLoading] = useState(false);

  const handleOpenPortal = async () => {
    try {
      setPortalLoading(true);
      const currentUser = auth.currentUser;
      if (!currentUser) {
        toast.error(isTr ? 'Oturum açmanız gerekiyor.' : 'You must be logged in.');
        return;
      }

      const idToken = await currentUser.getIdToken();
      const res = await fetch('/api/subscription/portal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      });

      const data = await res.json();
      const targetUrl = isLifetime ? (data.overviewUrl || data.url) : data.url;
      if (!res.ok || !data.success || !targetUrl) {
        throw new Error(data.message || 'Customer portal URL could not be generated.');
      }

      // Kişiye özel Paddle müşteri portalına yönlendir
      window.location.href = targetUrl;
    } catch (err) {
      console.error('[Profile] Customer portal error:', err);
      toast.error(isTr ? 'Abonelik portalı açılamadı: ' + err.message : 'Could not open portal: ' + err.message);
      setPortalLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmation = window.confirm(
      t('deleteAccountConfirm', 'Hesabınızı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')
    );
    if (!confirmation) return;

    try {
      setIsDeletingAccount(true);
      const loadingMsg = currentLanguage === 'en' ? 'Deleting account and cloud data...' : 'Hesabınız ve tüm verileriniz siliniyor...';
      toast.loading(loadingMsg, { id: 'delete-acc-loading' });

      const currentUser = auth.currentUser;
      const userObj = user || { uid: currentUser?.uid, email: currentUser?.email };
      const targetUid = currentUser?.uid || userObj?.uid;

      // 1. Firestore user_progress koleksiyonundaki belgeleri ({userId}_{termId}) where('userId', '==', targetUid) ile toplu sil (batch delete)
      if (targetUid) {
        try {
          const progressRef = collection(db, 'user_progress');
          const q = query(progressRef, where('userId', '==', targetUid));
          const snapshot = await getDocs(q);

          if (!snapshot.empty) {
            let batch = writeBatch(db);
            let uncommittedOps = 0;
            for (const docSnap of snapshot.docs) {
              batch.delete(docSnap.ref);
              uncommittedOps++;
              if (uncommittedOps === 400) {
                await batch.commit();
                batch = writeBatch(db);
                uncommittedOps = 0;
              }
            }
            if (uncommittedOps > 0) {
              await batch.commit();
            }
            console.log(`[Firestore] Deleted ${snapshot.size} user_progress documents for userId: ${targetUid}`);
          }
        } catch (dbErr) {
          console.error('[Firestore] Error batch deleting user_progress:', dbErr);
        }
      }

      // 2. LocalStorage ve diğer ilişkili kullanıcı verilerini temizle
      await clearAllUserData(currentUser || userObj);

      // 3. Firebase Auth kullanıcısını kalıcı olarak sil
      if (currentUser) {
        await currentUser.delete();
      }

      // 4. Yerel oturumu temizle ve yönlendir
      logout();
      toast.dismiss('delete-acc-loading');
      toast.success(t('accountDeleted', 'Hesabınız ve tüm verileriniz kalıcı olarak silindi.'));
      try { sessionStorage.removeItem('healthlex_selected_plan'); } catch (e) {}
      navigate('/register');
    } catch (err) {
      toast.dismiss('delete-acc-loading');
      console.error('Delete account error:', err);
      if (err?.code === 'auth/requires-recent-login') {
        toast.error(
          t('deleteAccountRelogin', 'Güvenlik nedeniyle, hesabınızı silmeden önce çıkış yapıp tekrar giriş yapmanız gerekmektedir.')
        );
      } else {
        toast.error(t('deleteAccountError', 'Hesap silinirken bir sorun oluştu.'));
      }
    } finally {
      setIsDeletingAccount(false);
    }
  };

  // Badge list definitions
  const badgeShowcaseList = [
    { id: 'first_step', icon: '🥇', name: t('badgeFirstStep', 'İlk Adım'), unlocked: stats.learnedTerms >= 1 },
    { id: 'quick_start', icon: '🎯', name: t('badgeQuickStart', 'Hızlı Başlangıç'), unlocked: stats.learnedTerms >= 10 },
    { id: 'quiz_master', icon: '🏆', name: t('badgeQuizMaster', 'Quiz Ustadı'), unlocked: stats.quizzesTaken >= 5 },
    { id: 'on_fire', icon: '🔥', name: t('badgeOnFire', 'Ateşli'), unlocked: streak.currentStreak >= 7 || streak.longestStreak >= 7 },
  ];

  const handleTogglePin = (badgeId) => {
    const isAlreadyPinned = pinnedBadges.includes(badgeId);
    if (!isAlreadyPinned && pinnedBadges.length >= 3) {
      toast.warning(t('maxBadgesWarning', 'En fazla 3 adet rozet vitrine ekleyebilirsiniz.'));
      return;
    }
    const updated = isAlreadyPinned
      ? pinnedBadges.filter(b => b !== badgeId)
      : [...pinnedBadges, badgeId];
    setPinnedBadges(updated);
    savePreferences({ pinnedBadges: updated });

    if (isAlreadyPinned) {
      toast.info(isTr ? 'Rozet vitrinden kaldırıldı.' : 'Badge removed from showcase.');
    } else {
      toast.success(isTr ? 'Rozet vitrine eklendi!' : 'Badge pinned to showcase!');
    }
  };

  const handleToggleEmail = () => {
    const val = !emailNotifications;
    setEmailNotifications(val);
    savePreferences({ emailNotifications: val });
  };

  return (
    <div className="profile-theme min-h-screen bg-[var(--paper)]">
      <main className="py-[36px] px-0">
        <div className="wrap">

          {/* Heading Section */}
          <div className="mb-[24px]">
            <div className="eyebrow text-[0.72rem] font-bold text-[var(--teal)] uppercase tracking-wider mb-1">{t('myAccount')}</div>
            <h1 className="font-serif font-semibold text-[1.7rem] leading-none mb-1 text-[var(--ink)]">{t('myProfile')}</h1>
            <p className="section-sub text-[0.86rem] text-[var(--muted)] m-0">
              {t('profileSubtitle')}
            </p>
          </div>

          {/* IDENTITY CARD */}
          <section className="mb-[36px]">
            <div className="card bg-white border border-[var(--line)] rounded-[var(--radius)] p-[28px]">
              <div className="identity flex justify-between items-start gap-[20px] flex-wrap">
                <div className="identity-left flex gap-[18px] items-center">
                  <div className="avatar-wrap relative flex-shrink-0">
                    <div className="avatar-lg w-[74px] h-[74px] rounded-full bg-gradient-to-br from-[var(--blue)] to-[var(--teal)] flex items-center justify-center text-white font-serif text-[1.7rem] font-bold">
                      {userName.charAt(0)}
                    </div>
                  </div>
                  <div>
                    <div className="id-name font-serif font-semibold text-[1.32rem] text-[var(--ink)] capitalize mb-1">
                      {userName}
                    </div>
                    <div className="id-meta flex gap-[16px] flex-wrap text-[0.86rem] text-[var(--muted)]">
                      <span className="flex items-center gap-[6px]">
                        <svg className="w-[14px] h-[14px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>
                        </svg>
                        {user?.email}
                      </span>
                      <span className="flex items-center gap-[6px]">
                        <svg className="w-[14px] h-[14px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
                        </svg>
                        {t('membership')} {formatDate(user?.joinDate)}
                      </span>
                    </div>
                    <div className="badge-pins flex gap-[8px] mt-[10px]">
                      {badgeShowcaseList
                        .filter(b => pinnedBadges.includes(b.id) && b.unlocked)
                        .map(b => (
                          <div className="pin-badge w-[30px] h-[30px] rounded-[8px] bg-[var(--paper-dim)] flex items-center justify-center text-[0.95rem]" key={b.id} title={b.name}>
                            {b.icon}
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
                <div className="identity-actions flex gap-[10px]">
                  <button 
                    onClick={() => setIsEditOpen(!isEditOpen)}
                    className="btn btn-outline bg-transparent border border-[var(--line)] text-[var(--ink)] hover:bg-[var(--paper-dim)] transition-all font-semibold rounded-[9px]"
                  >
                    {isEditOpen ? t('close') : t('edit')}
                  </button>
                  <button 
                    onClick={handleLogout}
                    className="btn btn-danger-outline bg-transparent border border-[#F0C9C0] text-[var(--coral)] hover:bg-[#FBEAE6] transition-all font-semibold rounded-[9px]"
                  >
                    {t('logout')}
                  </button>
                </div>
              </div>

              {/* Edit Panel */}
              <div className={`edit-panel ${isEditOpen ? 'open block' : 'hidden'} mt-[22px] pt-[20px] border-t border-[var(--line)]`}>
                <div className="field-row grid grid-cols-1 sm:grid-cols-2 gap-[14px] mb-[14px]">
                  <div className="field">
                    <label className="block text-[0.8rem] text-[var(--muted)] font-semibold mb-1">{t('fullName')}</label>
                    <input 
                      type="text" 
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="w-full p-[10px_12px] border border-[var(--line)] rounded-[8px] text-[0.92rem] bg-[var(--paper)] focus:outline-none focus:border-[var(--teal)]"
                    />
                  </div>
                  <div className="field">
                    <label className="block text-[0.8rem] text-[var(--muted)] font-semibold mb-1">{t('email')}</label>
                    <input 
                      type="email" 
                      value={editEmail}
                      disabled
                      title={t('emailCannotBeChanged')}
                      className="w-full p-[10px_12px] border border-[var(--line)] rounded-[8px] text-[0.92rem] bg-[var(--paper-dim)] text-[var(--muted)] cursor-not-allowed focus:outline-none"
                    />
                  </div>
                </div>
                <div className="edit-actions flex gap-[10px] mt-1.5">
                  <button 
                    onClick={handleSaveChanges}
                    disabled={saveLoading}
                    className="btn btn-primary bg-[var(--teal)] text-white hover:bg-[var(--teal-deep)] transition-all font-semibold rounded-[9px]"
                  >
                    {saveLoading ? t('saving') : t('saveChanges')}
                  </button>
                  <button 
                    onClick={() => {
                      setEditName(user?.name || '');
                      setIsEditOpen(false);
                    }}
                    className="btn btn-outline bg-transparent border border-[var(--line)] hover:bg-[var(--paper-dim)] transition-all font-semibold rounded-[9px]"
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* STATUS STRIP */}
          <section className="mb-[36px]">
            <div className="status-strip flex items-center justify-between gap-[14px] flex-wrap bg-[var(--paper-dim)] border border-dashed border-[var(--line)] rounded-[12px] p-[14px_20px] text-[0.87rem] text-[var(--muted)]">
              <span>
                <strong>{stats.learnedTerms}</strong> {t('termsCount', 'terim')} · 🔥 <strong>{streak.currentStreak} {t('daysStreak', 'gün')}</strong> {t('statusStripStreakText')}
              </span>
              <Link to="/progress" className="font-bold text-[var(--teal-deep)] hover:underline whitespace-nowrap">
                {t('progressPageLink')}
              </Link>
            </div>
          </section>

          {/* SUBSCRIPTION & PLAN SECTION */}
          <section className="mb-[36px]">
            <div className="card bg-white border border-[var(--line)] rounded-[var(--radius)] p-[28px]">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-[var(--line)]">
                <div>
                  <div className="eyebrow text-[0.72rem] font-bold text-[var(--teal)] uppercase tracking-wider mb-1">
                    {isTr ? 'Abonelik & Tarife' : 'Subscription & Plan'}
                  </div>
                  <h2 className="font-serif font-semibold text-[1.28rem] text-[var(--ink)] m-0">
                    {isTr ? 'Üyelik Durumunuz' : 'Your Membership Plan'}
                  </h2>
                </div>
                <div className="flex items-center gap-2 self-start sm:self-auto">
                  {isLifetime ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-purple-500/15 text-purple-700 dark:text-purple-300 border border-purple-500/30">
                      <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
                      {isTr ? 'Ömür Boyu VIP' : 'Lifetime VIP'}
                    </span>
                  ) : isTrialActive ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30">
                      <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                      {trialDaysLeft <= 1
                        ? (isTr ? 'Deneme (Son gün)' : 'Trial (Last day)')
                        : (isTr ? `Deneme (${trialDaysLeft} gün kaldı)` : `Trial (${trialDaysLeft} days left)`)}
                    </span>
                  ) : isBasic ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/30">
                      <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                      {isTr ? 'Temel · Aktif' : 'Basic · Active'}
                    </span>
                  ) : isProPlan ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      {isTr ? 'Aktif' : 'Active'}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-muted text-muted-foreground border border-border">
                      {isTr ? 'Ücretsiz Hesap' : 'Free Account'}
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-5 flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="max-w-xl">
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <span className="text-xl">
                      {isLifetime ? '👑' : isProPlan ? '⭐' : isBasic ? '⚡' : isTrialActive ? '⏳' : '🌱'}
                    </span>
                    <h3 className="text-[1.1rem] font-bold text-[var(--ink)] m-0">
                      {isLifetime
                        ? (isTr ? 'Ömür Boyu Plan' : 'Lifetime Plan')
                        : isTrialActive
                        ? (isBasic
                            ? (isTr ? 'Temel Plan (Ücretsiz Deneme)' : 'Basic Plan (Free Trial)')
                            : (isTr ? 'Pro Plan (Ücretsiz Deneme)' : 'Pro Plan (Free Trial)'))
                        : isProPlan
                        ? (isTr ? 'Yıllık Pro Plan' : 'Annual Pro Plan')
                        : isBasic
                        ? (isTr ? 'Temel Plan Üyesi' : 'Basic Plan Member')
                        : (isTr ? 'Ücretsiz Plan' : 'Free Plan')}
                    </h3>
                  </div>
                  <p className="text-[0.88rem] text-[var(--muted)] leading-relaxed m-0">
                    {isLifetime
                      ? (isTr
                          ? 'Tüm tıbbi terminoloji morfemlerine, 4 oyun moduna ve gelecekte eklenecek tüm özelliklere süresiz ömür boyu erişim.'
                          : 'Unlimited lifetime access to all medical terminology morphemes, game modes, and all future updates.')
                      : isTrialActive
                      ? (isBasic
                          ? (isTr
                              ? '3 günlük ücretsiz deneme sürecindesiniz. Tüm 16 kategori, 571+ morfem ve 4 oyun modunun tamamına sınırsız erişebilirsiniz.'
                              : 'You are on a 3-day free trial. Full access to all 16 categories, 571+ morphemes, and 4 game modes.')
                          : (isTr
                              ? '3 günlük ücretsiz deneme sürecindesiniz. 16 kategorinin tamamı, 571+ morfem ve 4 oyun moduna sınırsız erişebilirsiniz.'
                              : 'You are on a 3-day free trial. Full access to all 16 categories, 571+ morphemes, and 4 game modes.'))
                      : isProPlan
                      ? (isTr
                          ? 'Tüm tıbbi terminoloji morfemlerine, oyun modlarına ve detaylı analizlere sınırsız erişim.'
                          : 'Unlimited access to all medical terminology morphemes, game modes, and in-depth analyses.')
                      : isBasic
                      ? (isTr
                          ? 'Temel plandasınız: 16 kategori sözlüğü, 100 morfem ve sınırsız Flashcard & Eşleştirme açık. Tüm 571+ morfem ve Quiz için Pro\'ya geçebilirsiniz.'
                          : 'You are on the Basic plan: 16 categories glossary, 100 morphemes, and unlimited Flashcards & Matching unlocked. Upgrade to Pro for all 571+ morphemes and Quiz.')
                      : (isTr
                          ? 'Temel erişimdesiniz. 571 morfem kütüphanesi, 4 oyun modu ve seviye sisteminin tamamına sınırsız erişmek için Pro\'ya geçin.'
                          : 'You have trial access. Upgrade to Pro to unlock 571 morphemes, 4 game modes, and unlimited tracking.')}
                  </p>
                  {trialState.isActive && (
                    <div className="mt-3 text-xs font-semibold text-amber-700 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 px-3 py-1.5 rounded-lg inline-flex items-center gap-2">
                      <span>⏳</span>
                      <span>{trialState.summaryText(isTr ? 'tr' : 'en')}</span>
                    </div>
                  )}
                  {hasActiveSubscription && (
                    <p className="text-[0.78rem] text-[var(--muted)] mt-2.5 leading-normal opacity-90">
                      ℹ️ {isTr
                        ? 'Abonelik iptali veya fatura detaylarınız için Paddle portalını kullanabilir veya help@healthlexmed.com ile iletişime geçebilirsiniz.'
                        : 'For cancellation or invoice details, you can use the Paddle portal or contact help@healthlexmed.com.'}
                    </p>
                  )}
                </div>

                <div className="flex-shrink-0 flex flex-wrap items-center gap-2.5">
                  {hasActiveSubscription && (
                    <button
                      type="button"
                      onClick={handleOpenPortal}
                      disabled={portalLoading}
                      className="btn btn-outline inline-flex items-center gap-2 bg-transparent border border-[var(--line)] text-[var(--ink)] hover:bg-[var(--paper-dim)] hover:border-[var(--teal)] transition-all font-semibold rounded-[9px] px-4 py-2.5 text-sm cursor-pointer disabled:opacity-50"
                    >
                      {portalLoading ? (
                        <div className="w-4 h-4 border-2 border-[var(--teal)] border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <svg className="w-4 h-4 text-[var(--muted)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                      )}
                      {portalLoading
                        ? (isTr ? 'Portal Açılıyor...' : 'Opening Portal...')
                        : (isTr ? 'Aboneliği Yönet / İptal Et' : 'Manage / Cancel Subscription')}
                    </button>
                  )}

                  {isLifetime && (
                    <button
                      type="button"
                      onClick={handleOpenPortal}
                      disabled={portalLoading}
                      className="btn btn-outline inline-flex items-center gap-2 bg-transparent border border-[var(--line)] text-[var(--ink)] hover:bg-[var(--paper-dim)] hover:border-[var(--teal)] transition-all font-semibold rounded-[9px] px-4 py-2.5 text-sm cursor-pointer disabled:opacity-50"
                    >
                      {portalLoading ? (
                        <div className="w-4 h-4 border-2 border-[var(--teal)] border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <svg className="w-4 h-4 text-[var(--muted)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <polyline points="10 9 9 9 8 9" />
                        </svg>
                      )}
                      {portalLoading
                        ? (isTr ? 'Açılıyor...' : 'Opening...')
                        : (isTr ? 'Faturalarım / Makbuz' : 'Invoices / Receipts')}
                    </button>
                  )}

                  {!isLifetime && !isProPlan && (
                    <Link
                      to="/pricing"
                      className="btn btn-primary inline-flex items-center gap-2 bg-[var(--teal)] text-white hover:bg-[var(--teal-deep)] transition-all font-semibold rounded-[9px] px-5 py-2.5 text-sm shadow-sm"
                    >
                      <span>★</span>
                      {isTr ? "Pro'ya Yükselt →" : 'Upgrade to Pro →'}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* SHOWCASE BADGES SECTION */}
          <section className="mb-[36px]">
            <div className="card bg-white border border-[var(--line)] rounded-[var(--radius)] p-[28px]">
              <h2 className="font-serif font-semibold text-[1.2rem] text-[var(--ink)] mb-1">{t('showcaseBadges')}</h2>
              <p className="section-sub text-[0.86rem] text-[var(--muted)] m-0 mb-[18px]">
                {t('showcaseSub')}
              </p>
              <div className="showcase-grid grid grid-cols-2 md:grid-cols-4 gap-[14px]">
                {badgeShowcaseList.map((badge) => {
                  const isPinned = pinnedBadges.includes(badge.id);
                  return (
                    <div 
                      key={badge.id} 
                      onClick={() => badge.unlocked && handleTogglePin(badge.id)}
                      className={`showcase-item relative rounded-[11px] p-[18px_12px] text-center transition-all ${
                        badge.unlocked 
                          ? `cursor-pointer select-none hover:shadow-md ${
                              isPinned 
                                ? 'bg-amber-50/40 border-2 border-[var(--gold)] shadow-xs' 
                                : 'bg-[var(--paper)] border border-[var(--line)] hover:border-[var(--teal)]'
                            }` 
                          : 'bg-[var(--paper)] border border-[var(--line)] opacity-50 cursor-not-allowed select-none'
                      }`}
                      title={
                        badge.unlocked 
                          ? (isPinned 
                              ? (isTr ? 'Vitrinden kaldırmak için tıklayın' : 'Click to remove from showcase') 
                              : (isTr ? 'Vitrine eklemek için tıklayın' : 'Click to add to showcase')) 
                          : (isTr ? 'Bu rozet henüz kazanılmadı' : 'Badge not unlocked yet')
                      }
                    >
                      <div className="ic text-[1.6rem] mb-[6px] transform transition-transform group-hover:scale-110">{badge.icon}</div>
                      <div className="nm text-[0.8rem] font-semibold text-[var(--ink)] leading-tight">{badge.name}</div>
                      <div className="text-[0.68rem] font-medium mt-1.5">
                        {badge.unlocked ? (
                          isPinned ? (
                            <span className="text-[var(--gold)] font-bold">★ {isTr ? 'Vitrinde' : 'In Showcase'}</span>
                          ) : (
                            <span className="text-[var(--muted)] hover:text-[var(--teal)]">+ {isTr ? 'Vitrine Ekle' : 'Pin'}</span>
                          )
                        ) : (
                          <span className="text-[var(--muted)] opacity-60">🔒 {isTr ? 'Kilitli' : 'Locked'}</span>
                        )}
                      </div>
                      {badge.unlocked && (
                        <div 
                          className={`pin-toggle absolute top-[8px] right-[8px] w-[22px] h-[22px] rounded-full flex items-center justify-center border transition-all pointer-events-none ${
                            isPinned 
                              ? 'pinned bg-[var(--gold)] border-[var(--gold)] text-white shadow-xs' 
                              : 'bg-white border-[var(--line)] text-[var(--muted)]'
                          }`}
                        >
                          <svg className="w-[11px] h-[11px]" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2l2.9 6.6 7.1.6-5.4 4.7 1.7 6.9L12 17.3 5.7 20.8l1.7-6.9L2 9.2l7.1-.6z"/>
                          </svg>
                        </div>
                      )}
                    </div>
                  );
                })}
                <Link to="/progress" className="showcase-more flex items-center justify-center text-[0.83rem] text-[var(--teal-deep)] font-bold hover:underline">
                  {t('viewAll')}
                </Link>
              </div>
            </div>
          </section>

          {/* ACCOUNT SETTINGS SECTION */}
          <section className="mb-[36px]">
            <div className="card bg-white border border-[var(--line)] rounded-[var(--radius)] p-[28px]">
              <h2 className="font-serif font-semibold text-[1.2rem] text-[var(--ink)] mb-1">{t('accountSettings')}</h2>
              <p className="section-sub text-[0.86rem] text-[var(--muted)] m-0 mb-[18px]">
                {t('accountSettingsSub')}
              </p>

              {/* Interface Language Setting (Dropdown) */}
              <div className="setting-row flex justify-between items-center gap-[16px] py-[16px] border-b border-[var(--line)]">
                <div>
                  <div className="setting-label text-[0.92rem] font-semibold text-[var(--ink)]">{t('interfaceLanguage')}</div>
                  <div className="setting-sub text-[0.8rem] text-[var(--muted)] mt-0.5">{t('interfaceLanguageSub')}</div>
                </div>
                <div className="relative">
                  <select
                    value={currentLanguage}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="bg-white border border-[var(--line)] rounded-[9px] px-3.5 py-2 text-sm font-semibold text-[var(--ink)] focus:outline-none focus:border-[var(--teal)] cursor-pointer shadow-xs hover:border-[var(--teal)] transition-all"
                  >
                    <option value="tr">Türkçe</option>
                    <option value="en">English</option>
                  </select>
                </div>
              </div>

              {/* Security: Change Password */}
              <div className="setting-row flex justify-between items-center gap-[16px] py-[16px] border-b border-[var(--line)]">
                <div>
                  <div className="setting-label text-[0.92rem] font-semibold text-[var(--ink)]">{t('changePassword')}</div>
                  <div className="setting-sub text-[0.8rem] text-[var(--muted)] mt-0.5">{t('changePasswordSub')}</div>
                </div>
                <button 
                  onClick={handlePasswordReset}
                  className="btn btn-outline bg-transparent border border-[var(--line)] hover:bg-[var(--paper-dim)] transition-all font-semibold rounded-[9px]"
                >
                  {t('change')}
                </button>
              </div>

              {/* Export Data */}
              <div className="setting-row flex justify-between items-center gap-[16px] py-[16px] last:border-none">
                <div>
                  <div className="setting-label text-[0.92rem] font-semibold text-[var(--ink)]">
                    {isTr ? 'İlerleme Raporunu İndir (PDF)' : 'Download Progress Report (PDF)'}
                  </div>
                  <div className="setting-sub text-[0.8rem] text-[var(--muted)] mt-0.5 max-w-[420px]">
                    {isTr 
                      ? '16 kategori, öğrenilen terimler, rozetler ve çalışma istatistiklerinizi içeren resmi PDF raporu.' 
                      : 'Official PDF report containing 16 categories, learned terms, badges, and study stats.'}
                  </div>
                  <button
                    type="button"
                    onClick={handleExportJson}
                    className="text-[0.75rem] text-[var(--muted)] hover:text-[var(--teal)] underline mt-1.5 inline-block cursor-pointer transition-colors"
                  >
                    {isTr ? '⚙ Gelişmiş: Ham Verilerimi İndir (JSON)' : '⚙ Advanced: Download Raw Data (JSON)'}
                  </button>
                </div>
                <button 
                  type="button"
                  onClick={handleExportPdf}
                  disabled={isExportingPdf}
                  className="btn btn-outline bg-transparent border border-[var(--teal)] text-[var(--teal-deep)] hover:bg-[var(--teal)] hover:text-white transition-all font-semibold rounded-[9px] px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 flex-shrink-0 shadow-xs"
                >
                  {isExportingPdf ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-[var(--teal-deep)] border-t-transparent rounded-full animate-spin"></div>
                      <span>{isTr ? 'Hazırlanıyor...' : 'Generating...'}</span>
                    </>
                  ) : (
                    <>
                      <span>📄</span>
                      <span>{isTr ? 'PDF Raporu İndir' : 'Download PDF'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </section>

          {/* DANGER ZONE SECTION */}
          <section className="mb-0">
            <div className="card danger-card bg-white border border-[#F0C9C0] rounded-[var(--radius)] p-[28px]">
              <h2 className="font-serif font-semibold text-[1.2rem] text-[var(--coral)] mb-1">{t('dangerZone')}</h2>
              <p className="section-sub text-[0.86rem] text-[var(--muted)] m-0 mb-[18px]">{t('dangerZoneSub')}</p>
              
              <div className="setting-row flex justify-between items-center gap-[16px] py-[16px] last:border-none">
                <div>
                  <div className="setting-label text-[0.92rem] font-semibold text-[var(--ink)]">{t('deleteAccount')}</div>
                  <div className="setting-sub text-[0.8rem] text-[var(--muted)] mt-0.5">{t('deleteAccountSub')}</div>
                </div>
                <button 
                  onClick={handleDeleteAccount}
                  disabled={isDeletingAccount}
                  className="btn btn-danger-outline bg-transparent border border-[#F0C9C0] text-[var(--coral)] hover:bg-[#FBEAE6] disabled:opacity-50 disabled:cursor-not-allowed transition-all font-semibold rounded-[9px]"
                >
                  {isDeletingAccount
                    ? (currentLanguage === 'en' ? 'Deleting...' : 'Siliniyor...')
                    : t('deleteAccountBtn')}
                </button>
              </div>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
};